import * as React from "react";
import { bindAll } from "underscore";

import "@videojs/http-streaming";
import "video.js/dist/video-js.min.css";

import Utils from "pages/common/lib/grabyo-util";
import videojs from "pages/common/components/players/videojs";
import { MediaType } from "pages/campaign/lib/grab";
import {
    deserializeQueryParameters,
    parseUrl,
    generateRandomUrl
} from "util/string-utils";
import BrowserUtils, { isServer } from "util/browser-utils";
import type { AspectRatio } from "api/AspectRatio.types";

const TYPES = {
    HLS: `hls`,
    RTMP: `rtmp`,
    VIDEO: `video`
};

interface PlayerProps {
    type: string;
    aspectRatio?: AspectRatio;
    poster?: string;
    preload?: string;
    events?: Record<string, () => void>;
    id?: string;
    src: string;
    vtt?: string;
    cacheBuster?: string | number;
    withCacheBuster?: boolean;
}

// This is a minimal version that we use. Full documentation is here: https://docs.videojs.com/tutorial-options.html
interface VideoJSOptions {
    controls: boolean;
    fluid: boolean;
    aspectRatio: AspectRatio;
    poster: string;

    techOrder?: string[];
    html5?: {
        vhs: {
            withCredentials: boolean;
        };
    };
}

class PlayerComponent extends React.Component<
    PlayerProps,
    {
        mixedContent: boolean;
        mimeType?: string;
        type?: string;
        insecurePopUpLink?: string;
        withCredentials?: boolean;
    }
> {
    videoNode: any;
    player: any;
    el: HTMLDivElement;

    constructor(props: PlayerProps) {
        super(props);
        this.state = {
            mixedContent: false
        };
        bindAll(this, `onFirstPlay`, `onEnded`);
    }

    /* eslint-disable-next-line */
    UNSAFE_componentWillMount() {
        if (!isServer()) {
            this.setSourceState(this.props);
        }
    }

    componentDidMount() {
        if (!this.state.mixedContent && this.props.type === MediaType.CLIP) {
            const options: VideoJSOptions = {
                controls: true,
                fluid: true,
                aspectRatio: this.props.aspectRatio || `16:9`,
                poster: this.props.poster
            };

            switch (this.state.type) {
                case TYPES.RTMP:
                    throw new Error(`RTMP playback is no longer supported`);
                default:
                    Object.assign(options, {
                        techOrder: [`html5`],
                        html5: {
                            vhs: {
                                withCredentials: true
                            }
                        }
                    });
            }

            this.setCredentialsState()
                .then(() => {
                    if (
                        Object.prototype.hasOwnProperty.call(options, `html5`)
                    ) {
                        options.html5.vhs.withCredentials =
                            this.state.withCredentials;
                    }
                })
                .then(() => this.initPlayer(options));
        }
    }

    initPlayer(options: VideoJSOptions) {
        if (!document.body.contains(this.videoNode)) {
            setTimeout(() => this.initPlayer(options), 250);
        } else {
            this.player = videojs(this.videoNode, options, () => {
                // According to video.js autoplay best practices there's higher chances for the video
                // to autoplay if `play` is called programmatically
                if (this.props.preload !== `none`) {
                    this.player.play();
                }

                if (this.state.type === TYPES.HLS) {
                    this.setHlsChunksProxy();
                }

                if (this.props.events) {
                    Object.entries(this.props.events).map(
                        ([eventName, callback]) =>
                            this.player.on(eventName, callback)
                    );
                }

                // If it has an ID let's track some analytics
                if (this.props.id) {
                    App.Events.trigger(`video:onRender`, this);
                    this.player.on(`play`, this.onFirstPlay);
                    this.player.on(`pause`, () =>
                        App.Events.trigger(`video:onPause`, this)
                    );
                    this.player.on(`ended`, this.onEnded);
                }
            });
        }
    }

    onFirstPlay() {
        App.Events.trigger(`video:onPlay`, this);
        this.player.off(`play`, this.onFirstPlay);
    }

    onEnded() {
        App.Events.trigger(`video:onComplete`, this);
        this.player.on(`play`, this.onFirstPlay);
    }

    componentWillUnmount() {
        if (this.player && this.props.type === MediaType.CLIP) {
            this.destroyPlayer();
        }
    }

    /* eslint-disable-next-line */
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.type === MediaType.CLIP) {
            if (nextProps.src !== this.props.src) {
                this.setSourceState(nextProps);
            }
            if (this.player) {
                this.player.poster(nextProps.poster || "");
            }

            if (
                nextProps.src !== this.props.src ||
                nextProps.vtt !== this.props.vtt
            ) {
                this.refreshVideoSource(nextProps);
            }
        }
    }

    setSourceState(props) {
        if (BrowserUtils.isRtmpUrl(props.src)) {
            this.setState({
                type: TYPES.RTMP,
                mimeType: `rtmp/mp4`
            });
        } else if (BrowserUtils.isHlsUrl(props.src)) {
            this.setState({
                type: TYPES.HLS,
                mimeType: `application/x-mpegURL`
            });
        } else {
            this.setState({
                type: TYPES.VIDEO,
                mimeType: `video/mp4`
            });
        }

        if (
            this.state.type !== TYPES.RTMP &&
            BrowserUtils.isHttpMixedContentRequest(this.props.src)
        ) {
            this.setState({
                mixedContent: true,
                insecurePopUpLink: `http://${location.host}/standalonehlsstream.jsp?streamUrl=${this.props.src}`
            });
        }
    }

    setHlsChunksProxy() {
        // This proxies all the requests and sends all the query params to the TS chunk
        // needed for AKAMAI Secure HD

        const manifestUrlParams = parseUrl(this.props.src);
        const manifestQueryParams = deserializeQueryParameters(
            manifestUrlParams.search
        );

        if (this.player.tech().vhs) {
            this.player.tech().vhs.xhr.beforeRequest = function (options) {
                const url = options.uri;

                const urlParams = parseUrl(url);
                const urlQueryParams = deserializeQueryParameters(
                    urlParams.search
                );

                const mergedParams = Object.assign(
                    manifestQueryParams,
                    urlQueryParams
                );

                urlParams.search = $.param(mergedParams);
                options.uri = urlParams.href;

                return options;
            };
        }
    }

    setCredentialsState() {
        if (this.state.type === TYPES.HLS) {
            return new Promise((res) => {
                $.ajax({
                    url: this.props.src,
                    type: `GET`,
                    dataType: `text`,
                    xhrFields: {
                        withCredentials: true
                    }
                })
                    .done(() => this.setState({ withCredentials: true }))
                    .catch(() => this.setState({ withCredentials: false }))
                    .always(() => res(undefined));
            });
        } else {
            return Promise.resolve();
        }
    }

    refreshVideoSource(props) {
        this.player.src({
            type: this.state.mimeType,
            src: props.src,
            vtt: props.vtt
        });
    }

    render() {
        const { cacheBuster, src, withCacheBuster = true } = this.props;
        // Add a cachebuster url param to work around CORS caching issues
        // This value should match on the server and browser
        const sourceUrl = withCacheBuster
            ? generateRandomUrl(src, cacheBuster)
            : src;

        return (
            <div className="video-player" ref={(el) => (this.el = el)}>
                {this.props.type === MediaType.CLIP ? (
                    !this.state.mixedContent ? (
                        <div data-vjs-player>
                            <video
                                preload={this.props.preload || `auto`}
                                ref={(node) => (this.videoNode = node)}
                                className="video-js"
                            >
                                <source
                                    src={sourceUrl}
                                    type={this.state.mimeType}
                                />
                                {this.props.vtt ? (
                                    <track
                                        kind="subtitles"
                                        src={this.props.vtt}
                                    />
                                ) : null}
                            </video>
                        </div>
                    ) : (
                        <div className="grInsecureVideoMessage">
                            <p>
                                Source stream is not secure (HTTPS) and can not
                                be embedded for preview.
                                <a
                                    className="js-open-insecure-video"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={this.state.insecurePopUpLink}
                                    onClick={this.openInsecureModal}
                                >
                                    Click here
                                </a>
                                {` `}
                                to preview the source in a pop-up (insecurely).
                            </p>
                        </div>
                    )
                ) : (
                    <img src={src} />
                )}
            </div>
        );
    }

    openInsecureModal(evt) {
        evt.preventDefault();
        Utils.popUp({
            url: evt.target.href,
            scrollbars: false,
            width: 622,
            height: 350
        });
    }

    destroyPlayer() {
        if (this.player) {
            this.player.dispose();
            delete this.player;
        }
    }
}

export default PlayerComponent;
